import * as THREE from "three";
import { MonsterModel } from "../render/MonsterModel";
import type { MonsterDef } from "../rpg/Monster";
import { moveWithCollision, type Circle, type SolidTest } from "./Collision";
import { ACTOR_SCALE, TILE } from "./constants";
import type { EncounterGroup } from "./TileMap";

export interface FieldMonsterOptions {
  /** Spawn point, in world units. */
  x: number;
  z: number;
  /** What the party will actually fight when they touch it. */
  group: EncounterGroup;
  /** Definition used for the visible body. */
  lead: MonsterDef;
  aggroRadius?: number;
  /** How far it will chase before giving up and going home. */
  leash?: number;
  speed?: number;
}

type State = "idle" | "wander" | "chase" | "return" | "cooldown";

/**
 * A monster you can see on the field, wandering its patch until you get close.
 * Touching one starts the battle — the modern alternative to an invisible step
 * counter, and the reason the map itself has to be interesting to walk through.
 */
export class FieldMonster {
  readonly object3D = new THREE.Group();
  readonly model: MonsterModel;
  readonly group: EncounterGroup;

  radius: number;
  /** Cleared once the party has beaten it; the scene then schedules a respawn. */
  defeated = false;

  private state: State = "idle";
  private timer = Math.random() * 2;
  private readonly homeX: number;
  private readonly homeZ: number;
  private target: { x: number; z: number } | null = null;
  private readonly aggroRadius: number;
  private readonly leash: number;
  private readonly speed: number;
  private alertLift = 0;

  constructor(options: FieldMonsterOptions) {
    this.group = options.group;
    this.homeX = options.x;
    this.homeZ = options.z;
    this.aggroRadius = options.aggroRadius ?? TILE * 4.5;
    this.leash = options.leash ?? TILE * 9;
    // Faster than a walk, slower than a sprint: you can get away, but only by
    // deciding to.
    this.speed = options.speed ?? 5.2;

    const appearance = options.lead.appearance;
    this.model = new MonsterModel(appearance);
    this.model.object3D.scale.multiplyScalar(ACTOR_SCALE);
    this.object3D.add(this.model.object3D);
    this.object3D.position.set(options.x, 0, options.z);

    this.radius = 0.45 * ACTOR_SCALE * (appearance.scale ?? 1);
  }

  get x(): number {
    return this.object3D.position.x;
  }

  get z(): number {
    return this.object3D.position.z;
  }

  get height(): number {
    return this.model.height * ACTOR_SCALE;
  }

  asCircle(): Circle {
    return { x: this.x, z: this.z, radius: this.radius };
  }

  /** Stops it chasing for a while — used after the party runs away. */
  cool(seconds = 5): void {
    this.state = "cooldown";
    this.timer = seconds;
  }

  distanceTo(point: { x: number; z: number }): number {
    return Math.hypot(point.x - this.x, point.z - this.z);
  }

  update(dt: number, player: { x: number; z: number }, isSolid: SolidTest, obstacles: readonly Circle[]): void {
    this.model.update(dt);

    if (this.defeated) return;

    const toPlayer = this.distanceTo(player);
    const fromHome = Math.hypot(this.x - this.homeX, this.z - this.homeZ);

    switch (this.state) {
      case "cooldown":
        this.timer -= dt;
        if (this.timer <= 0) this.state = "idle";
        break;

      case "idle":
      case "wander":
        if (toPlayer < this.aggroRadius) {
          this.state = "chase";
          this.alertLift = 1;
          break;
        }
        this.timer -= dt;
        if (this.target) {
          if (this.step(this.target, dt, this.speed * 0.45, isSolid, obstacles) < 0.2) this.target = null;
        } else if (this.timer <= 0) {
          this.timer = 1.5 + Math.random() * 2.5;
          const angle = Math.random() * Math.PI * 2;
          const reach = TILE * 1.5 * Math.random();
          this.target = { x: this.homeX + Math.sin(angle) * reach, z: this.homeZ + Math.cos(angle) * reach };
          this.state = "wander";
        }
        break;

      case "chase":
        if (fromHome > this.leash || toPlayer > this.aggroRadius * 1.8) {
          this.state = "return";
          this.target = { x: this.homeX, z: this.homeZ };
          break;
        }
        this.step(player, dt, this.speed, isSolid, obstacles);
        break;

      case "return":
        if (toPlayer < this.aggroRadius * 0.8) {
          this.state = "chase";
          break;
        }
        if (!this.target || this.step(this.target, dt, this.speed * 0.6, isSolid, obstacles) < 0.3) {
          this.target = null;
          this.state = "idle";
          this.timer = 1;
        }
        break;
    }

    // A little hop when it first notices you.
    if (this.alertLift > 0) {
      this.alertLift = Math.max(0, this.alertLift - dt * 2.2);
      this.object3D.position.y = Math.sin((1 - this.alertLift) * Math.PI) * 0.5;
    } else {
      this.object3D.position.y = 0;
    }
  }

  /** Moves towards a point and returns the distance remaining. */
  private step(
    to: { x: number; z: number },
    dt: number,
    speed: number,
    isSolid: SolidTest,
    obstacles: readonly Circle[],
  ): number {
    const dx = to.x - this.x;
    const dz = to.z - this.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 1e-3) return 0;

    const move = Math.min(distance, speed * dt);
    const next = moveWithCollision(
      { x: this.x, z: this.z },
      (dx / distance) * move,
      (dz / distance) * move,
      this.radius,
      isSolid,
      obstacles,
    );
    this.object3D.position.x = next.x;
    this.object3D.position.z = next.z;

    // Face where it is going.
    const wanted = Math.atan2(dx, dz);
    let delta = wanted - this.object3D.rotation.y;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    this.object3D.rotation.y += delta * Math.min(1, dt * 6);

    return distance - move;
  }

  /** Fades out on defeat; the scene respawns a fresh one later. */
  async playDefeat(): Promise<void> {
    this.defeated = true;
    this.object3D.visible = false;
  }

  dispose(): void {
    this.model.dispose();
  }
}
